/**
 * In-memory access token used by API requests after login.
 * Kept outside Redux so axios helpers can read it without importing the store.
 */
import axios, { type AxiosInstance } from 'axios';

let accessToken: string | null = null;

function clearAuthorizationHeader(headers: unknown): void {
  if (!headers || typeof headers !== 'object') return;
  const h = headers as {
    delete?: (key: string) => void;
    common?: { delete?: (key: string) => void; Authorization?: unknown };
    Authorization?: unknown;
  };

  if (typeof h.delete === 'function') {
    h.delete('Authorization');
  } else {
    delete h.Authorization;
  }

  if (h.common) {
    if (typeof h.common.delete === 'function') {
      h.common.delete('Authorization');
    } else {
      delete h.common.Authorization;
    }
  }
}

function syncAxiosAuth(client: AxiosInstance, token: string | null): void {
  if (token) {
    client.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    clearAuthorizationHeader(client.defaults.headers);
  }
}

/** Clients that must drop Authorization on logout (registered by http.ts). */
const authClients = new Set<AxiosInstance>([axios]);

export function registerAuthClient(client: AxiosInstance): void {
  authClients.add(client);
  syncAxiosAuth(client, accessToken);
}

export function setAccessToken(token: string | null): void {
  accessToken = token && token.trim().length > 0 ? token : null;
  for (const client of authClients) {
    syncAxiosAuth(client, accessToken);
  }
}

/** Erase token from memory and all registered axios clients. */
export function clearAccessToken(): void {
  setAccessToken(null);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getAuthorizationHeader(): string | null {
  return accessToken ? `Bearer ${accessToken}` : null;
}

let interceptorsInstalled = false;

/**
 * Install a request interceptor on the DEFAULT axios instance so bare
 * `axios.*` calls (not just the shared `http` client) carry the current
 * bearer token. Safe to call more than once — the interceptor is only added
 * on the first invocation.
 */
export function installAuthInterceptors(): void {
  if (interceptorsInstalled) return;
  interceptorsInstalled = true;

  axios.interceptors.request.use((config) => {
    const authorization = getAuthorizationHeader();
    if (authorization) {
      config.headers.set('Authorization', authorization);
    } else {
      // Explicitly strip any stale Authorization after logout.
      config.headers.delete('Authorization');
    }
    return config;
  });
}

/**
 * Resolve the current access token for callers that can't rely on axios
 * interceptors (e.g. a raw `fetch`). Returns null when no user is signed in.
 */
export function ensureToken(): Promise<string | null> {
  return Promise.resolve(getAccessToken());
}
